import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import Select from 'react-select';
import CircularProgress from '@mui/material/CircularProgress';
import { Scatter } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, LinearScale, PointElement, Tooltip as ChartTooltip, Legend } from 'chart.js';

import { fetchLatestReportDate as fetchLatestReportDateService } from 'services/modules/rhymscan/report';
import { fetchMitreMappings as fetchECSMitreMappingsService} from 'services/modules/ecs/data';
import { fetchMitreMappings as fetchRSMitreMappingsService } from 'services/modules/rhymscan/data';
import { fetchMitreMappings as fetchVAPTMitreMappingsService } from 'services/modules/vapt/data';
import { fetchCompanySelectedStandards as fetchCompanyStandardsService } from 'services/modules/cap/standardservice';
import { fetchMitreMappings as fetchMitreMappingsService } from 'services/modules/cap/assessmentservice';

// Register Chart.js components
ChartJS.register(LinearScale, PointElement, LineElement, ChartTooltip, Legend);

const ScatterGraph = () => {
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [selectedStandard, setSelectedStandard] = useState("");
  const [loading, setLoading] = useState(false);

  const [mitrelist, setMitreList] = useState([]);
  const [capMitrelist, setCAPMitreList] = useState([]);
  const [ecsApplication, setEcsApplication] = useState(false);
  const [rsApplication, setRSApplication] = useState(false);

  // Redux selectors
  const reportDateContainer = useSelector((state) => state.report.latestReportDate);
  const authenticatedUserContainer = useSelector((state) => state.auth.user);
  const ecsMitremappingsContainer = useSelector(state => state.ecsData.mitremappings);
  const rsMitremappingsContainer = useSelector(state => state.rsData.mitremappings);
  const rsReportDateContainer = useSelector((state) => state.rsReport.latestReportDate);
  const vaptMitremappingsContainer = useSelector(state => state.vaptData.mitremappings);


  // Redux dispatch
  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedCompanyContainer) {
      setEcsApplication(false);
      setRSApplication(false);

      const fetchCompanyReport = async () => {
        const domain = selectedCompanyContainer.domain;
        let params = { domain: domain };
        dispatch(fetchLatestReportDateService({ params }));
      };

      if (selectedCompanyContainer?.companyid === authenticatedUserContainer?.company?.id) {
        authenticatedUserContainer?.company?.applications?.forEach(element => {
          if (element.slug === "crs") {
            setEcsApplication(true);
          }
          else if(element.slug === "rhymscan"){
            setRSApplication(true);
          }
          else if(element.slug === "vapt"){
            let params = {
              "id":selectedCompanyContainer.companyid,
            }
            dispatch(fetchVAPTMitreMappingsService({ params }));
          }
        });
        // In the other case it finds whether the customers have CRQ service or not
      } else {
        if (authenticatedUserContainer?.company?.customers?.length > 0) {
          const companyCustomers = authenticatedUserContainer.company.customers;
          const customersWithSameCompanyId = companyCustomers.filter(customer => customer.id === selectedCompanyContainer.companyid);
          if (customersWithSameCompanyId.length > 0) {
            customersWithSameCompanyId[0].applications.forEach(element => {
              if (element.slug === "crs") {
                setEcsApplication(true);
              }
              else if(element.slug === "rhymscan"){
                setRSApplication(true);
              }
              else if(element.slug === "vapt"){
                let params = {
                  "id":selectedCompanyContainer.companyid,
                }
                dispatch(fetchVAPTMitreMappingsService({ params }));
              }
            });
          }
        }
      }

      fetchCompanyReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyContainer]);

  // useEffect hook fetches the threat actors data based on domain and date
  useEffect(() => {
    const fetchECSMitreData = async() => {
      let params = {
        "domain":selectedCompanyContainer.domain,
        "date":reportDateContainer
      }
      dispatch(fetchECSMitreMappingsService({ params }));
    };
    if (ecsApplication && reportDateContainer){
      fetchECSMitreData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDateContainer]);

  // useEffect hook fetches the threat actors data based on domain and date
  useEffect(() => {
    const fetchRSMitreData = async() => {
      let params = {
        "domain":selectedCompanyContainer.domain,
        "date":rsReportDateContainer
      }
      dispatch(fetchRSMitreMappingsService({ params }));
    };
    if (rsApplication && rsReportDateContainer){
      fetchRSMitreData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rsReportDateContainer]);

  const generateFindingTypeOptions = () => {
    const appOptions = authenticatedUserContainer?.company?.applications?.map(app => ({
      value: app.slug,
      label: app.label || app.slug.toUpperCase() // Adjust label based on app properties
    })) || [];
    return appOptions;
  };
  
  const findingTypeOptions = generateFindingTypeOptions();

  const techniques = [
    'Initial access', 'Execution', 'Persistence', 'Privilege escalation',
    'Defense evasion', 'Credential access', 'Discovery', 'Lateral movement',
    'Collection', 'Command and Control', 'Exfiltration', 'Impact'
  ];

  const defaultSelectedOption = findingTypeOptions.find(option => option.value === findingTypeOptions[0].value);
  const [selectedFindingType, setSelectedFindingType] = useState(defaultSelectedOption);
  const [findingtype, setFindingType] = useState(defaultSelectedOption.value);

  useEffect(() => {
    if (selectedCompanyContainer?.companyid !== null) {
      fetchStandardsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyContainer]);

  const fetchStandardsData = async () => {
    setLoading(true);
    try {
      setSelectedStandards([]);
      let params = { companyid: selectedCompanyContainer.companyid };
      const response = await fetchCompanyStandardsService({ params });
      if (response !== '') {
        let standards = response.data.map(e => ({ value: e.id, label: e.name }));
        if (standards.length > 0) {
          setSelectedStandards(standards);
          setSelectedStandard(standards[0]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch standards:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStandard !== "") {
      fetchMitreMappingsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStandard]);

  const fetchMitreMappingsData = async () => {
    setLoading(true);
    try {
      let params = { companyid: selectedCompanyContainer.companyid, standardid: selectedStandard.value };
      const mitreresponse = await fetchMitreMappingsService(params);
      if (mitreresponse?.data?.data?.length > 0) {
        // setMitreList(mitreresponse.data.data);
        setCAPMitreList(mitreresponse.data.data);
      }
      else{
        setCAPMitreList([]);
        // setMitreList([]);
      }
    } catch (error) {
      console.error("Failed to fetch mitre mappings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let combinedThreatActors = [];
    
    if(findingtype === "crs" && ecsMitremappingsContainer){
      for(const single_mapping of ecsMitremappingsContainer){
        combinedThreatActors.push({
          score: single_mapping.score,
          control: single_mapping.alert,
          mitre: single_mapping.mitre,
        });
      }
    }
    if(findingtype === "rhymscan" && rsMitremappingsContainer){
      for(const single_mapping of rsMitremappingsContainer){
        combinedThreatActors.push({
          score: single_mapping.score,
          control: single_mapping.alert,
          mitre: single_mapping.mitre,
        });
      }
    }
    if(findingtype === "vapt" && vaptMitremappingsContainer){
      for(const single_mapping of vaptMitremappingsContainer){
        combinedThreatActors.push({
          score: single_mapping.score,
          control: single_mapping.alert,
          mitre: single_mapping.mitre,
        });
      }
    }
    if(findingtype === "cap" && capMitrelist){
      combinedThreatActors = [...combinedThreatActors, ...capMitrelist];
    }

    if(combinedThreatActors){
      setMitreList(combinedThreatActors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ecsMitremappingsContainer, capMitrelist, selectedFindingType, vaptMitremappingsContainer, rsMitremappingsContainer]);


  const findingTypeChange = (selectedOption) => {
    setSelectedFindingType(selectedOption);
    setFindingType(selectedOption.value);
  };

  const applyJitter = (value) => value + (Math.random() - 0.5) * 2;

  const getTechniqueName = (mitreValue) => {
    const normalizedMitreValue = mitreValue
      .toLowerCase()
      .replace(/&/g, 'and') // Replace "&" with "and" for uniformity
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim();
  
    // Define standard technique names in a consistent format
    const standardizedTechniques = techniques.map(technique =>
      technique.toLowerCase()
    );
  
    // Try to match the normalized mitreValue to the standardized techniques list
    const match = standardizedTechniques.find(
      technique => technique === normalizedMitreValue
    );
  
    return match ? techniques[standardizedTechniques.indexOf(match)] : mitreValue;
  };

  const scatterData = {
    datasets: [
      {
        label: 'Compliance Controls',
        data: mitrelist
          .filter(item => item.score && item.mitre)
          .map(item => ({
            x: getTechniqueName(item.mitre),
            y: applyJitter(parseFloat(item.score)),
            control: item.control
          })),
        backgroundColor: mitrelist.map(item => {
          const score = parseFloat(item.score);
          if(findingtype === "cap"){
            if (score <= 40) return '#f44336';
            if (score > 40 && score <= 75) return '#ff9800';
            return '#4caf50';
          }
          else{
            if (score <= 4) return '#f44336';
            if (score > 4 && score <= 7) return '#ff9800';
            return '#4caf50';
          }
        }),
        // borderColor: 'rgba(0, 0, 0, 0.1)',
        pointRadius: 4
      }
    ]
  };

  const techniquesOrder = [
    '', 'Initial access', 'Execution', 'Persistence', 'Privilege escalation',
    'Defense evasion', 'Credential access', 'Discovery', 'Lateral movement',
    'Collection', 'Command and Control', 'Exfiltration', 'Impact'
  ];

  const options = {
    scales: {
      x: {
        type: 'category',
        labels: techniquesOrder,
        title: {
          display: true,
          text: 'MITRE Tactics'
        },
        // grid: {
        //   display: false
        // }
        ticks: {
          autoSkip: false,
          maxRotation: 90,
          minRotation: 0
      }
      },
      y: {
        beginAtZero: true,
        // max: 10,
        title: {
          display: true,
          text: 'Score'
        },
        // grid: {
        //   display: false
        // }
      },
    },
    plugins: {
      legend: {
        display: false // Disable clicking on the legend label
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const dataPoint = scatterData.datasets[0].data[tooltipItem.dataIndex];
            return `${dataPoint.control}: ${parseInt(dataPoint.y)}`;
          }
        }
      },
      // Set background color of the chart
      datalabels: {
        color: '#f7f8f9'
    }
    },
    backgroundColor: '#f7f8f9',
  };

  return (
    <div id="subcontrolsdiv" className="mitre-subcontrols" >
      <div className="isp-dashboard-title mt-4">MITRE Vulnerability Mapping</div>
      <div className="row justify-between mb-2">
      <div className="intput_field">
          <Select
            value={selectedFindingType}
            onChange={findingTypeChange}
            options={findingTypeOptions}
            placeholder="Finding Type..."
          />
        </div>
        <div className="intput_field">
          {findingtype === "cap" && (<Select
            value={selectedStandard}
            onChange={(standard) => setSelectedStandard(standard)}
            options={selectedStandards}
            placeholder="Standards..."
          />)}
        </div>
        
      </div>
      {loading ? (
        <div className="text-center mt-4">
          <CircularProgress size={60} />
        </div>
      ) : (
        <>
          <div className="chart-container" style={{ position: 'relative', height: '40vh', width: '80vw', backgroundColor:'#f7f8f9' }}>
            <Scatter data={scatterData} options={options} width={600} />
          </div>
          {/* Custom Legend */}
          <div className="legend-container" style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginRight: '15px' }}>
              <span style={{ width: '12px', height: '12px', backgroundColor: '#f44336', display: 'inline-block', marginRight: '5px' }}></span>
              Needs Urgent Attention 
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginRight: '15px' }}>
              <span style={{ width: '12px', height: '12px', backgroundColor: '#ff9800', display: 'inline-block', marginRight: '5px' }}></span>
              Needs Improvement
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ width: '12px', height: '12px', backgroundColor: '#4caf50', display: 'inline-block', marginRight: '5px' }}></span>
              Doing Well
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ScatterGraph;